import {
  CircleCheck,
  CircleDashed,
  CircleSlash,
  Loader2,
  MoreHorizontal,
  RotateCcw,
} from "lucide-react";
import type React from "react";
import { memo } from "react";
import { Link } from "wouter";

import { Loader } from "@/components/loader/Loader.tsx";
import { cardSurfaceClass, clickableCardClass } from "@/components/threads/shared.tsx";
import { formatDateForThreadList } from "@/components/threads/util.ts";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { cn } from "@/lib/utils.ts";
import { TASK_ASSIGNEE_LABELS, TASK_STATUS_LABELS, taskActions } from "@/shared/taskActions.ts";
import type { TaskListItemResp, TaskResp, TaskStatus } from "@/types/tasks.ts";
import { useUpdateTaskStatus } from "./useTasks.ts";

/**
 * Status reads as copy rather than as a colored tag, matching how a thread's
 * status renders — an icon and colored text, no pill chrome.
 */
const TASK_STATUS: Record<TaskStatus, { icon: typeof CircleCheck; className: string }> = {
  open: { icon: CircleDashed, className: "text-blue-700 dark:text-blue-400" },
  done: { icon: CircleCheck, className: "text-green-700 dark:text-green-400" },
  cancelled: { icon: CircleSlash, className: "text-muted-foreground" },
};

/** Each move's icon, keyed by the status it moves the task to. */
const ACTION_ICONS: Record<TaskStatus, typeof CircleCheck> = {
  done: CircleCheck,
  cancelled: CircleSlash,
  open: RotateCcw,
};

/** The overflow menu's moves: the closing verb the row didn't promote, or reopening. */
function overflowActions(task: TaskResp) {
  const actions = taskActions(task);
  return task.status === "open" ? actions.slice(1) : actions;
}

/**
 * Keeps a control's click from reaching the `<Link>` the account list wraps rows
 * in. Typed to the React click deliberately: Radix's `onSelect` hands you a
 * `menu.itemSelect` CustomEvent instead, where `preventDefault` means "keep the
 * menu open" and `stopPropagation` does nothing to the click that navigates.
 */
function stopRowNavigation(event: React.MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * A fixed slot the leading glyph is centred in, so the title starts at the same
 * x on every row and never moves as a row changes state.
 *
 * Sized to the widest thing it holds, the action button: 16px icon + its `p-1`
 * + the 1px transparent border every `Button` carries = 26px. Matching that by
 * hand on the other two states is what the first attempt did, and it was 2px
 * short — the border is easy to miss and would drift again the moment the
 * button's base classes change. A slot that owns the width can't.
 */
const GLYPH_SLOT = "flex size-6.5 shrink-0 items-center justify-center";

const GLYPH_SLOT_COMPACT = "flex size-5 shrink-0 items-center justify-center";

/**
 * The row's leading glyph: the status icon, and on an open task the button that
 * closes it. Reopening is rare and deliberate, so it stays in the menu.
 */
const TaskStatusControl = ({
  task,
  onAct,
  pending,
  compact,
}: {
  task: TaskResp;
  onAct: (status: TaskStatus) => void;
  pending: boolean;
  compact: boolean;
}) => {
  const status = TASK_STATUS[task.status];
  const StatusIcon = status.icon;
  const slot = compact ? GLYPH_SLOT_COMPACT : GLYPH_SLOT;
  const iconSize = compact ? "size-3.5" : "size-4";

  // The glyph is where the change was asked for, so it is where the wait
  // belongs — and it covers a reopen from the menu, whose row has no button.
  if (pending) {
    return (
      <span className={slot}>
        <Loader2
          aria-label="Saving"
          className={cn(iconSize, "animate-spin text-muted-foreground")}
        />
      </span>
    );
  }

  if (task.status !== "open") {
    return (
      <span className={slot}>
        <StatusIcon className={cn(iconSize, status.className)} />
      </span>
    );
  }

  const [action] = taskActions(task);
  const ActionIcon = ACTION_ICONS[action.status];
  return (
    <Button
      type="button"
      variant="ghost"
      size="action"
      aria-label={action.label}
      title={action.label}
      className={cn(slot, "group/status", compact && "p-0.5")}
      data-statsig-name="task-primary-action"
      onClick={(event) => {
        stopRowNavigation(event);
        onAct(action.status);
      }}
    >
      {/* Preview the move on keyboard focus too, not hover alone. */}
      <StatusIcon
        className={cn(
          iconSize,
          "group-hover/status:hidden group-focus-visible/status:hidden",
          status.className,
        )}
      />
      <ActionIcon
        className={cn(
          iconSize,
          "hidden group-hover/status:block group-focus-visible/status:block",
          TASK_STATUS[action.status].className,
        )}
      />
    </Button>
  );
};

/** Everything the row can do that isn't its one promoted action. */
const TaskRowMenu = ({
  task,
  onAct,
  disabled,
  compact,
}: {
  task: TaskResp;
  onAct: (status: TaskStatus) => void;
  disabled: boolean;
  compact: boolean;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn("shrink-0 text-muted-foreground", compact ? "size-5" : "size-7")}
        aria-label="Task actions"
        data-statsig-name="task-row-menu"
        disabled={disabled}
        onClick={stopRowNavigation}
      >
        <MoreHorizontal className={compact ? "size-3.5" : "size-4"} />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {overflowActions(task).map((action) => {
        const ActionIcon = ACTION_ICONS[action.status];
        return (
          <DropdownMenuItem
            key={action.status}
            data-statsig-name={`task-action-${action.status}`}
            // The menu portals, but React synthetic events still travel the React
            // tree — so the click reaches the row's `<Link>` unless stopped here.
            // `stopPropagation` only: `preventDefault` would cancel the selection.
            onClick={(event) => event.stopPropagation()}
            onSelect={() => onAct(action.status)}
          >
            <ActionIcon className="size-4" />
            {action.label}
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  </DropdownMenu>
);

const TaskRow = ({
  item,
  showThread,
  compact,
  onSelectThread,
}: {
  item: TaskListItemResp;
  showThread: boolean;
  compact: boolean;
  onSelectThread?: (threadId: number) => void;
}) => {
  const { task, thread } = item;
  const status = TASK_STATUS[task.status];
  const updateStatus = useUpdateTaskStatus();

  const act = (next: TaskStatus) =>
    updateStatus.mutate({ taskId: task.id, threadId: task.threadId, status: next });

  const row = (
    <div
      className={cn(
        "flex items-center",
        // The app's one card definition, rather than a bordered `rounded-lg` of
        // this list's own: a task row is a card on a canvas page like a routine
        // or a contact, and it was the only one drawing itself differently.
        // `compact` is the omnibox panel's inline list, which is not a card at
        // all and keeps its bare rows.
        compact
          ? "gap-2 py-0.5 transition-colors"
          : cn("gap-3", showThread ? clickableCardClass : cardSurfaceClass),
      )}
    >
      <TaskStatusControl
        task={task}
        onAct={act}
        pending={updateStatus.isPending}
        compact={compact}
      />
      <div className="min-w-0 flex-1">
        <span className={cn("block truncate", compact ? "text-xs" : "text-sm font-medium")}>
          {task.title}
        </span>
        {showThread && (
          <span className="block truncate text-xs text-muted-foreground">{thread.title}</span>
        )}
      </div>
      {!compact && (
        <div className="flex shrink-0 items-center gap-2 text-xs">
          <span className={cn("w-18 truncate", status.className)}>
            {TASK_STATUS_LABELS[task.status]}
          </span>
          <span className="w-22 truncate text-muted-foreground">
            {TASK_ASSIGNEE_LABELS[task.assignee]}
          </span>
          <span className="w-20 truncate text-right text-muted-foreground">
            {formatDateForThreadList(new Date(task.createdAt))}
          </span>
        </div>
      )}
      <TaskRowMenu task={task} onAct={act} disabled={updateStatus.isPending} compact={compact} />
    </div>
  );

  // The href stays even when a host handles the click: a modifier-click skips
  // wouter's handler entirely, so ⌘-click still opens the thread's own page in a
  // new tab. Only the plain click is claimed.
  return showThread ? (
    <Link
      href={`/threads/${thread.id}`}
      onClick={
        onSelectThread
          ? (event) => {
              // A modifier or a middle button is a request for a second place
              // to put this, which the host has none of. Let the href answer
              // those the way it would anywhere else — the comment above was
              // already promising this, but `preventDefault` ran first and
              // cancelled the new tab before the browser could open it.
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              if (event.button !== 0) return;
              event.preventDefault();
              onSelectThread(thread.id);
            }
          : undefined
      }
    >
      {row}
    </Link>
  ) : (
    row
  );
};

/**
 * The chrome both task dialogs wear, so the account-wide list and one thread's
 * list are the same object at the same size wherever you open them. Task rows
 * are wide — five fields plus two controls — so they get the roomier `3xl`
 * rather than the dialog default's 32rem.
 */
export const TASK_DIALOG_CONTENT_CLASS = "flex max-h-[85vh] flex-col overflow-hidden sm:max-w-3xl";

/**
 * Renders task rows for the account list or one thread, including account-list
 * navigation. Keeps loading, request failure, and a genuine empty result
 * visually distinct.
 */
export const TaskList = memo(function TaskList({
  tasks,
  isLoading,
  isError,
  onRetry,
  showThread,
  emptyMessage,
  compact = false,
  onSelectThread,
}: {
  tasks: TaskListItemResp[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Show each task's thread, and link the row to it. The list view only. */
  showThread: boolean;
  /**
   * Open a row's thread here instead of navigating to its page. A host with a
   * canvas passes its own opener, so the row lands on that canvas rather than
   * replacing the whole layout with the standalone thread page.
   */
  onSelectThread?: (threadId: number) => void;
  /** What an empty list says. Omit to render nothing and say it yourself. */
  emptyMessage?: string;
  /** Hide metadata and reduce spacing. */
  compact?: boolean;
}) {
  const stateClass = compact ? "px-2 py-1" : "px-2 py-6";

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center", compact ? "h-8" : "h-16")}>
        <Loader />
      </div>
    );
  }

  if (isError && tasks.length === 0) {
    return (
      <div className={cn("flex flex-col items-center gap-3 text-center", stateClass)}>
        <p className="text-sm text-muted-foreground">Couldn't load tasks. Check your connection.</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          data-statsig-name="tasks-retry"
        >
          Try again
        </Button>
      </div>
    );
  }

  if (tasks.length === 0) {
    if (emptyMessage === undefined) return null;
    return (
      <p className={cn("text-center text-sm text-muted-foreground", stateClass)}>{emptyMessage}</p>
    );
  }

  return (
    <div className={cn("flex flex-col", compact ? "gap-0.5" : "gap-1.5")}>
      {tasks.map((item) => (
        <TaskRow
          key={item.task.id}
          item={item}
          showThread={showThread}
          compact={compact}
          onSelectThread={onSelectThread}
        />
      ))}
    </div>
  );
});
